/*
 * @prop --component-button-color-background: Background color of primary button
 * @prop --component-button-color-background-hover: Background color of primary button on hover
 * @prop --component-button-color-background-active: Background color of primary button in active state
 * @prop --component-button-color-text: Text color of primary button
 * @prop --component-button-color-text-hover: Text color of primary button
 * @prop --component-button-color-icon: Icon color of primary button
 * @prop --component-button-color-icon-hover: Icon color of primary button on hover
 * @prop --component-button-secondary-color-background: Background color of secondary button
 * @prop --component-button-secondary-color-background-hover: Background color of secondary button on hover
 * @prop --component-button-secondary-color-background-active: Background color of secondary button in active state
 * @prop --component-button-secondary-color-border: Border color of secondary button
 * @prop --component-button-secondary-color-text: Text color of secondary button
 * @prop --component-button-secondary-color-icon: Icon color of secondary button
 * @prop --component-button-cta-color-background: Background color of cta button
 * @prop --component-button-cta-color-background-hover: Background color of cta button on hover
 * @prop --component-button-cta-color-background-active: Background color of cta button in active state
 * @prop --component-button-cta-color-text: Text color of cta button
 * @prop --component-button-cta-color-icon: Icon color of cta button
 * @prop --component-button-font-family: Font family of button
 * @prop --component-button-padding-horizontal: Custom horizontal padding for transparent Button
 * @prop --component-button-padding-vertical: Custom vertical padding for transparent Button
 */

//@todo clean up what is not needed
@mixin focus-color($color) {
  .button__text::after {
    border: 1px dashed $color;
  }
}

$button-spacing-top-bottom: var(--component-button-padding-vertical, 14px);
$button-spacing-left-right: var(--component-button-padding-horizontal, 16px);

:host {
  display: inline-block;

  button {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    justify-content: center;
    background-color: var(
      --component-button-color-background,
      var(--current-color-primary, $ui-blue)
    );
    color: var(
      --component-button-color-text,
      var(--current-color-primary-inverted, $ui-white)
    );
    font-family: var(
      --component-button-font-family,
      var(--skin-font-family-primary, $font-family-sans-serif)
    );
    font-size: 1rem;
    font-weight: normal;
    line-height: 1.27rem; //firefox crop some letters like g with 1.25rem
    border-radius: 0.25rem;
    overflow: hidden;
    padding: $button-spacing-top-bottom $button-spacing-left-right;
    cursor: pointer;
    text-align: center;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    width: 100%;

    .button__text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }

    ui-icon {
      width: 20px;
      height: 20px;
      margin-right: 8px;
    }

    &:focus-visible {
      outline: auto;
    }
  }
}

:host(:empty) {
  ui-icon {
    margin-right: 0;
  }
}

:host(:hover) {
  button {
    color: var(
      --component-button-color-text-hover,
      var(--skin-color-primary-inverted, $ui-white)
    );
    background-color: var(
      --component-button-color-background-hover,
      var(--skin-color-primary-light, $ui-blue-minus-1)
    );
  }
}

:host([variant='primary']) {
  ui-icon {
    --component-icon-color: var(
      --component-button-color-icon,
      var(--current-color-primary-inverted, #{$ui-white})
    );
  }
}

:host([variant='primary']:hover) {
  ui-icon {
    --component-icon-color: var(
      --component-button-color-icon-hover,
      var(--skin-color-primary-inverted, #{$ui-white})
    );
  }
}

:host([variant='primary']:active) {
  button {
    background-color: var(
      --component-button-color-background-active,
      var(--skin-color-primary-shade, $ui-blue-plus-2)
    );
  }
}

:host([variant='plain']),
:host([variant='transparent']) {
  button {
    color: var(
      --component-button-secondary-color-text,
      var(--current-color-primary, $ui-blue)
    );
    background-color: unset;
    border-radius: unset;

    ui-icon {
      --component-icon-color: var(
        --component-button-secondary-color-icon,
        var(--current-color-primary, #{$ui-blue})
      );
    }

    .button__text {
      text-decoration: underline;
    }
  }
}

:host([variant='plain']) {
  margin: -#{$button-spacing-top-bottom} -#{$button-spacing-left-right};
}

:host([variant='transparent']:hover),
:host([variant='plain']:hover) {
  button {
    color: var(
      --component-button-color-background-hover,
      var(--skin-color-primary-light, $ui-blue-minus-1)
    );

    ui-icon {
      --component-icon-color: var(
        --component-button-color-background-hover,
        var(--skin-color-primary-light, #{$ui-blue-minus-1})
      );
    }
  }
}

:host([variant='plain']:active),
:host([variant='transparent']:active),
:host([variant='secondary']:active) {
  button {
    color: var(--current-color-primary-shade, $ui-blue-plus-2);

    ui-icon {
      --component-icon-color: var(
        --component-button-secondary-color-background-active,
        var(--current-color-primary-shade, #{$ui-blue-plus-2})
      );
    }
  }
}

:host([variant='secondary']) {
  button {
    background-color: var(
      --component-button-secondary-color-background,
      var(--current-color-primary-inverted, transparent)
    );
    border: 1px solid
      var(
        --component-button-secondary-color-border,
        var(--current-color-primary, $ui-blue)
      );
    color: var(
      --component-button-secondary-color-text,
      var(--current-color-primary, $ui-blue)
    );
    padding: calc(#{$button-spacing-top-bottom} - 1px)
      calc(#{$button-spacing-left-right} - 1px);

    ui-icon {
      --component-icon-color: var(
        --component-button-secondary-color-icon,
        var(--current-color-primary, #{$ui-blue})
      );
    }
  }
}

:host([variant='secondary']:hover) {
  button {
    position: relative;

    ::after {
      background-color: var(
        --component-button-secondary-color-background-hover,
        var(--current-color-primary, rgba($ui-blue-minus-1, 0.05))
      );
      opacity: 0.05;
      position: absolute;
      content: ' ';
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
}

:host([variant='secondary']:active) {
  button {
    border-color: var(--current-color-primary-shade, $ui-blue-plus-2);
  }
}

:host([variant='cta']) {
  button {
    background-color: var(
      --component-button-cta-color-background,
      var(--skin-color-accent, $ui-green)
    );
    color: var(
      --component-button-cta-color-text,
      var(--skin-color-accent-inverted, $ui-white)
    );

    ui-icon {
      --component-icon-color: var(
        --component-button-cta-color-icon,
        var(--skin-color-primary-inverted, #{$ui-white})
      );
    }
  }
}

:host([variant='cta']:hover) {
  button {
    background-color: var(
      --component-button-cta-color-background-hover,
      var(--skin-color-accent-light, $ui-green-minus-1)
    );
  }
}

:host([variant='cta']:active) {
  button {
    background-color: var(
      --component-button-cta-color-background-active,
      var(--skin-color-accent-shade, $ui-green-plus-1)
    );
  }
}

:host([variant='primary'][disabled]),
:host([variant='primary'][disabled]:active),
:host([variant='cta'][disabled]) {
  button {
    background-color: var(--ui-color-grey-400);
    color: var(--ui-color-white, $ui-white);
    /* stylelint-disable-next-line plugin/no-unsupported-browser-features */
    cursor: not-allowed;

    ui-icon {
      --component-icon-color: var(--ui-color-white, $ui-white);
    }
  }
}

:host([variant='secondary'][disabled]),
:host([variant='secondary'][disabled]:active),
:host([variant='transparent'][disabled]),
:host([variant='transparent'][disabled]:active),
:host([variant='plain'][disabled]),
:host([variant='plain'][disabled]:active) {
  button {
    color: var(--ui-color-grey-400);
    /* stylelint-disable-next-line plugin/no-unsupported-browser-features */
    cursor: not-allowed;

    ui-icon {
      --component-icon-color: var(--ui-color-grey-400);
    }
  }
}

:host([variant='secondary'][disabled]),
:host([variant='secondary'][disabled]:active) {
  button {
    border-color: var(--ui-color-grey-400);
  }
}

:host([variant='secondary'][disabled]:hover) {
  button {
    ::after {
      background-color: transparent;
    }
  }
}
